import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { ApiRequestOptions, useApiClient } from "../client";
import {
  ApplyManagedImageEditRequestDto,
  ManagedImageResponseDto,
} from "../interfaces/media.interface";
import {
  ApplyOpponentLogoEditRequestDto,
  CreateOpponentRequestDto,
  OpponentListResponseDto,
  OpponentsUploadLogoFormData,
  OpponentResponseDto,
  UpdateOpponentRequestDto,
} from "../interfaces/opponents.interface";

// Query keys for opponents-related queries
export const opponentsKeys = {
  all: ["opponents"] as const,
  list: (search?: string) => [...opponentsKeys.all, "list", search] as const,
  detail: (id: string) => [...opponentsKeys.all, "detail", id] as const,
};

function invalidateOpponentsQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: opponentsKeys.all });
}

/**
 * Find all opponents
 */
export function useOpponentsFind(
  search?: string,
  options?: Omit<
    UseSuspenseQueryOptions<OpponentListResponseDto[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: opponentsKeys.list(search),
    queryFn: () => {
      const queryParams = search ? `?search=${encodeURIComponent(search)}` : "";
      return apiRequest<OpponentListResponseDto[]>(`/opponents${queryParams}`);
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useOpponentsFind
 */
export function useOpponentsFindQuery(
  search?: string,
  options?: Omit<
    UseQueryOptions<OpponentListResponseDto[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: opponentsKeys.list(search),
    queryFn: () => {
      const queryParams = search ? `?search=${encodeURIComponent(search)}` : "";
      return apiRequest<OpponentListResponseDto[]>(`/opponents${queryParams}`);
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find one opponent
 */
export function useOpponentsFindOne(
  id: string,
  options?: Omit<
    UseSuspenseQueryOptions<OpponentResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: opponentsKeys.detail(id),
    queryFn: () => apiRequest<OpponentResponseDto>(`/opponents/${id}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useOpponentsFindOne
 */
export function useOpponentsFindOneQuery(
  id: string,
  options?: Omit<
    UseQueryOptions<OpponentResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: opponentsKeys.detail(id),
    queryFn: () => apiRequest<OpponentResponseDto>(`/opponents/${id}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Create a new opponent
 */
export function useOpponentsCreate(
  options?: Omit<
    UseMutationOptions<OpponentResponseDto, Error, CreateOpponentRequestDto>,
    "mutationFn"
  >,
  requestOptions?: ApiRequestOptions,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateOpponentRequestDto) =>
      apiRequest<OpponentResponseDto>(`/opponents`, "POST", data, undefined, requestOptions),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Update one opponent
 */
export function useOpponentsUpdate(
  options?: Omit<
    UseMutationOptions<
      void,
      Error,
      { id: string; data: UpdateOpponentRequestDto }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: UpdateOpponentRequestDto }) =>
      apiRequest<void>(`/opponents/${id}`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete one opponent
 */
export function useOpponentsDelete(
  options?: Omit<UseMutationOptions<void, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<void>(`/opponents/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete multiple opponents
 */
export function useOpponentsBulkDelete(
  options?: Omit<UseMutationOptions<void, Error, { ids: string[] }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiRequest<void>(`/opponents/bulk-delete`, "POST", { ids }),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Upload opponent logo
 */
export function useOpponentsUploadLogo(
  options?: Omit<
    UseMutationOptions<
      ManagedImageResponseDto,
      Error,
      { id: string; data: OpponentsUploadLogoFormData }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: OpponentsUploadLogoFormData;
    }) => {
      const formData = new FormData();
      if (data.file) {
        formData.append("file", data.file);
      }
      return apiRequest<ManagedImageResponseDto>(`/opponents/${id}/logo`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete opponent logo
 */
export function useOpponentsDeleteLogo(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/opponents/${id}/logo`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOpponentsApplyLogoEdit(
  options?: Omit<
    UseMutationOptions<ManagedImageResponseDto, Error, { id: string; data: ApplyOpponentLogoEditRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: ApplyManagedImageEditRequestDto }) =>
      apiRequest<ManagedImageResponseDto>(`/opponents/${id}/logo/edit`, "POST", data),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOpponentsRestoreOriginalLogo(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/opponents/${id}/logo/restore-original`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidateOpponentsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOpponentsPreviewBackgroundRemoval(
  options?: Omit<UseMutationOptions<Blob, Error, { file: File }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: ({ file }: { file: File }) => {
      const formData = new FormData();
      formData.append("file", file);

      return apiRequest<Blob>(
        `/opponents/media/preview-background-removal`,
        "POST",
        formData,
        undefined,
        { responseType: "blob", throwOnError: true }
      );
    },
  });
}
